import { SdkContext } from '../internal'
import type {
  AdjustmentScope,
  AdjustmentStatus,
  AdjustmentType,
  PunchCollector,
  TimeAdjustment,
  TimeAejExport,
  TimeCsvExport,
  TimeHoliday,
  TimeRules,
  TimeRuleSet,
  TimeRulesState,
  Timesheet,
  TimesheetMonth,
  TimesheetsPage,
  PunchScope,
  TimeAfdExport,
  TimeClock,
  TimeEmployee,
  TimeEmployeeInput,
  TimeEstablishment,
  TimeEstablishmentInput,
  TimeEstablishmentPrefill,
  TimeFileLink,
  TimeGeofenceInput,
  TimeGeofenceRefusal,
  TimePunch,
  TimePunchPage,
  TimeSpacePerson,
  TimeTrackingSpace
} from '@/types/timeTracking'

const toPage = (result: unknown): TimePunchPage => {
  if (Array.isArray(result)) return { items: result as TimePunch[], pagination: null }
  const r = result as { items?: TimePunch[]; data?: TimePunch[]; pagination?: TimePunchPage['pagination'] } | null
  return { items: r?.items || r?.data || [], pagination: r?.pagination ?? null }
}

const toList = <T>(result: unknown): T[] => {
  if (Array.isArray(result)) return result as T[]
  const r = result as { items?: T[]; data?: T[] } | null
  return r?.items || r?.data || []
}

/**
 * F-51 Time tracking, phase 1A (records). The community comes from
 * X-CommunityId (never the path). Management (owner, admin, finance) and who
 * may read a punch are the server's: `canManage`/`canPunch` per space, 404 for
 * a punch the caller may not read. There is no route to change or delete a
 * punch, and so no method here.
 */
export class TimeTrackingResource {
  constructor(private ctx: SdkContext) {}

  /** The server's time: the punch is stamped with it, never the device's. */
  async clock(): Promise<TimeClock> {
    return this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/clock' })
  }

  async establishmentPrefill(): Promise<TimeEstablishmentPrefill> {
    return this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/establishments/prefill' })
  }

  async listEstablishments(): Promise<TimeEstablishment[]> {
    return toList<TimeEstablishment>(await this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/establishments' }))
  }

  async getEstablishment(id: string): Promise<TimeEstablishment> {
    return this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/establishments/{id}' }, { id })
  }

  async createEstablishment(body: TimeEstablishmentInput): Promise<TimeEstablishment> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/establishments', hasBody: true }, { body })
  }

  async updateEstablishment(id: string, body: Partial<TimeEstablishmentInput>): Promise<TimeEstablishment> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/time-tracking/establishments/{id}', hasBody: true }, { id, body })
  }

  /** AFD (layout 004) and its `.sha256`, as private links that expire in 5 minutes. */
  async exportAfd(id: string, body: { from: string; to: string }): Promise<TimeAfdExport> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/establishments/{id}/afd', hasBody: true }, { id, body })
  }

  async getSpace(spaceId: string): Promise<TimeTrackingSpace> {
    return this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/spaces/{spaceId}' }, { spaceId })
  }

  /** 409 `spaceHasPunches` when the space already has punches on another establishment. */
  async linkSpace(spaceId: string, establishmentId: string): Promise<TimeTrackingSpace> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/time-tracking/spaces/{spaceId}', hasBody: true }, { spaceId, body: { establishmentId } })
  }

  async spacePeople(spaceId: string): Promise<TimeSpacePerson[]> {
    return toList<TimeSpacePerson>(await this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/spaces/{spaceId}/people' }, { spaceId }))
  }

  async listEmployees(params: { establishmentId?: string; spaceId?: string }): Promise<TimeEmployee[]> {
    return toList<TimeEmployee>(await this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/employees' }, { query: params }))
  }

  async createEmployee(body: TimeEmployeeInput & { establishmentId: string; memberId: string }): Promise<TimeEmployee> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/employees', hasBody: true }, { body })
  }

  async updateEmployee(id: string, body: Partial<TimeEmployeeInput>): Promise<TimeEmployee> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/time-tracking/employees/{id}', hasBody: true }, { id, body })
  }

  /** The record ends (no more punches); the punches already made stay for good. */
  async removeEmployee(id: string, responsibleCpf?: string): Promise<{ id: string; removed: boolean }> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/time-tracking/employees/{id}', hasBody: true },
      { id, body: responsibleCpf ? { responsibleCpf } : {} }
    )
  }

  /** The person saw the IP and location notice (repeating keeps the first date). */
  async acknowledgeNotice(spaceId: string): Promise<{ spaceId: string; locationNoticeAt: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/notice', hasBody: true }, { body: { spaceId } })
  }

  async listPunches(params: { spaceId?: string; scope?: PunchScope; from?: string; to?: string; memberId?: string; page?: number; limit?: number } = {}): Promise<TimePunchPage> {
    return toPage(await this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/punches' }, { query: params }))
  }

  /** The same `clientRequestId` answers the punch already saved (`replayed: true`) instead of a second one. */
  async punch(body: {
    spaceId: string
    clientRequestId: string
    collector: PunchCollector
    latitude?: number
    longitude?: number
    accuracyMeters?: number
  }): Promise<TimePunch> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/punches', hasBody: true }, { body })
  }

  /* ---------- F-106 15: virtual fence ---------- */

  /** Owner and admin (403 `geofenceDenied`); 409 `noEstablishment` while the space has no establishment. */
  async setGeofence(spaceId: string, body: TimeGeofenceInput): Promise<TimeTrackingSpace> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/time-tracking/spaces/{spaceId}/geofence', hasBody: true }, { spaceId, body })
  }

  /** Back to "nothing refuses a punch"; punches and refusals already saved stay. */
  async removeGeofence(spaceId: string): Promise<TimeTrackingSpace> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/time-tracking/spaces/{spaceId}/geofence' }, { spaceId })
  }

  /** Management: punches the BLOCK fence refused, newest first (default the last 30 days). */
  async geofenceRefusals(params: { spaceId?: string; from?: string; to?: string; memberId?: string; page?: number; limit?: number } = {}): Promise<{ items: TimeGeofenceRefusal[]; pagination: TimePunchPage['pagination'] }> {
    const result = await this.ctx.call<unknown>({ httpMethod: 'GET', path: '/time-tracking/geofence-refusals' }, { query: params })
    if (Array.isArray(result)) return { items: result as TimeGeofenceRefusal[], pagination: null }
    const r = result as { items?: TimeGeofenceRefusal[]; data?: TimeGeofenceRefusal[]; pagination?: TimePunchPage['pagination'] } | null
    return { items: r?.items || r?.data || [], pagination: r?.pagination ?? null }
  }

  async getPunch(id: string): Promise<TimePunch> {
    return this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/punches/{id}' }, { id })
  }

  /** The receipt PDF as a private link that expires in 5 minutes: ask again for each download. */
  async receipt(id: string): Promise<TimeFileLink & { punchId: string; nsr: number }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/punches/{id}/receipt' }, { id })
  }

  /* ---------- Phase 1B and 2: treatment ---------- */

  /** Management. `current` is the rule in force today (the person's exception with `memberId`). */
  async rules(params: { spaceId: string; memberId?: string }): Promise<TimeRulesState> {
    return this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/rules' }, { query: params })
  }

  /** Insert only: a correction is another version. 422 `monthClosed` for a date in (or before) a closed month. */
  async saveRules(body: { spaceId: string; memberId?: string | null; effectiveFrom: string; rules: TimeRules }): Promise<TimeRuleSet> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/rules', hasBody: true }, { body })
  }

  async holidays(params: { spaceId: string; year?: number }): Promise<TimeHoliday[]> {
    return toList<TimeHoliday>(await this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/holidays' }, { query: params }))
  }

  async addHoliday(body: { spaceId: string; date: string; name: string }): Promise<TimeHoliday> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/holidays', hasBody: true }, { body })
  }

  async removeHoliday(id: string): Promise<{ id: string; removed: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/time-tracking/holidays/{id}' }, { id })
  }

  async adjustments(params: { spaceId: string; scope?: AdjustmentScope; status?: AdjustmentStatus; month?: string }): Promise<TimeAdjustment[]> {
    return toList<TimeAdjustment>(await this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/adjustments' }, { query: params }))
  }

  /** `ADD` takes `time` (community time zone, `YYYY-MM-DDThh:mm`); `DISREGARD` takes `targetPunchId`. */
  async requestAdjustment(body: { spaceId: string; type: AdjustmentType; time?: string; targetPunchId?: string; reason: string }): Promise<TimeAdjustment> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/adjustments', hasBody: true }, { body })
  }

  async getAdjustment(id: string): Promise<TimeAdjustment> {
    return this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/adjustments/{id}' }, { id })
  }

  /** The direct leader (or the management for someone without one). A rejection needs `note`. */
  async decideAdjustment(id: string, body: { decision: 'approve' | 'reject'; note?: string }): Promise<TimeAdjustment> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/adjustments/{id}/decision', hasBody: true }, { id, body })
  }

  async cancelAdjustment(id: string): Promise<TimeAdjustment> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/adjustments/{id}/cancel', hasBody: true }, { id, body: {} })
  }

  /** The person (default), their leaders and the management; anyone else: 404. */
  async timesheet(params: { spaceId: string; month?: string; memberId?: string }): Promise<Timesheet> {
    return this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/timesheet' }, { query: params })
  }

  async timesheets(params: { spaceId: string; month?: string; scope?: 'team' | 'all' }): Promise<TimesheetsPage> {
    return this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/timesheets' }, { query: params })
  }

  async months(spaceId: string): Promise<TimesheetMonth[]> {
    return toList<TimesheetMonth>(await this.ctx.call({ httpMethod: 'GET', path: '/time-tracking/months' }, { query: { spaceId } }))
  }

  async closeMonth(spaceId: string, month: string): Promise<TimesheetMonth> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/months/close', hasBody: true }, { body: { spaceId, month } })
  }

  /** Owner or admin, from the most recent backwards, with a reason. */
  async reopenMonth(spaceId: string, month: string, reason: string): Promise<TimesheetMonth> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/months/reopen', hasBody: true }, { body: { spaceId, month, reason } })
  }

  async acknowledgeMonth(spaceId: string, month: string): Promise<{ spaceId: string; month: string; acknowledgedAt: string; replayed?: boolean }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/months/acknowledge', hasBody: true }, { body: { spaceId, month } })
  }

  async exportCsv(spaceId: string, month: string): Promise<TimeCsvExport> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/exports/csv', hasBody: true }, { body: { spaceId, month } })
  }

  /** AEJ (layout 002) and its `.sha256`, as private links that expire in 5 minutes. */
  async exportAej(id: string, body: { from: string; to: string }): Promise<TimeAejExport> {
    return this.ctx.call({ httpMethod: 'POST', path: '/time-tracking/establishments/{id}/aej', hasBody: true }, { id, body })
  }
}
